/**
 * Utility to extract organizer context for AI Assistant
 * Fetches assignments, due dates, and messages from the organizer
 */

import { db } from '@/database/Database';

export interface Assignment {
  title: string;
  dueDate: string;
  status: string;
  description?: string;
}

export interface OrganizerData {
  className: string;
  assignments: Assignment[];
  upcomingDeadlines: string[];
  recentMessages: string[];
}

/**
 * Get real organizer data from database based on class/tab
 */
export async function getOrganizerContext(tabLabel: string, userId: string): Promise<OrganizerData> {
  try {
    // Query notebooks from database
    const result = await db.query(
      'SELECT content FROM notebooks WHERE user_id = ? ORDER BY updated_at DESC LIMIT 1',
      [userId]
    );
    
    console.log('[Organizer Context] Raw query result:', result);
    
    // Handle different response formats
    const rows = result?.rows || result || [];
    
    if (!Array.isArray(rows) || rows.length === 0) {
      console.log('[Organizer Context] No notebooks found for user:', userId);
      return {
        className: tabLabel,
        assignments: [],
        upcomingDeadlines: [],
        recentMessages: [],
      };
    }
    
    const notebooks = JSON.parse(rows[0].content as string);
    console.log('[Organizer Context] Notebooks parsed:', notebooks);
    console.log('[Organizer Context] Looking for tab:', tabLabel);
    console.log('[Organizer Context] Total notebooks:', notebooks.length);
    
    // WORKAROUND: Since notebooks don't have proper names, aggregate ALL assignments from ALL notebooks
    // Extract from notebook categories → buckets → upcoming items
    const assignments: Assignment[] = [];
    const upcomingDeadlines: string[] = [];
    const now = new Date();
    
    console.log('[Organizer Context] Processing all notebooks for deadlines...');
    
    // Loop through ALL notebooks and extract items with due dates
    for (const notebook of notebooks) {
      if (!notebook || !notebook.categories || !Array.isArray(notebook.categories)) continue;
      
      console.log(`[Organizer Context] Notebook has ${notebook.categories.length} categories`);
      
      // Loop through categories (discussion, assignment, video, etc.)
      for (const category of notebook.categories) {
        if (!category.buckets) continue;
        
        // Check upcoming bucket for items with due dates
        const upcomingItems = category.buckets.upcoming || [];
        console.log(`[Organizer Context] Category "${category.id}" has ${upcomingItems.length} upcoming items`);
        
        for (const item of upcomingItems) {
          // Accept ANY item with a dueDate property
          if (item.dueDate && item.status === 'upcoming') {
            const dueDate = new Date(item.dueDate);
            const assignment = {
              title: item.title || item.text || 'Untitled Item',
              dueDate: dueDate.toLocaleDateString(),
              status: item.status || 'pending',
              description: item.description || item.notes,
            };
            
            assignments.push(assignment);
            
            // Add to upcoming deadlines if due within 2 weeks
            if (dueDate > now) {
              const daysUntil = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
              if (daysUntil <= 14) {
                upcomingDeadlines.push(`${assignment.title} due ${assignment.dueDate}`);
              }
            }
          }
        }
      }
    }
    
    console.log(`[Organizer Context] Found ${assignments.length} total assignments, ${upcomingDeadlines.length} upcoming deadlines`);
    
    // Sort assignments by due date
    assignments.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
    
    return {
      className: tabLabel, // Use room name since notebooks lack names
      assignments: assignments.slice(0, 5), // Top 5 upcoming
      upcomingDeadlines: upcomingDeadlines.slice(0, 3), // Top 3
      recentMessages: [], // Can add later from discussion items
    };
  } catch (error) {
    console.error('[Organizer Context] Error fetching data:', error);
    
    // Check if it's a CORS error
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.error('🚫 CORS/Network Error: Cannot connect to database from browser. This is expected in production.');
      console.error('💡 Workaround: Database queries work on localhost. In production, organizer data will need a server-side proxy.');
    }
    
    return {
      className: tabLabel,
      assignments: [],
      upcomingDeadlines: [],
      recentMessages: [],
    };
  }
}

/**
 * Calculate time until deadline
 */
export function getDeadlineUrgency(dueDate: string): 'urgent' | 'soon' | 'upcoming' {
  const due = new Date(dueDate);
  const now = new Date();
  const hoursUntil = (due.getTime() - now.getTime()) / (1000 * 60 * 60);
  
  if (hoursUntil < 24) return 'urgent';
  if (hoursUntil < 72) return 'soon';
  return 'upcoming';
}

/**
 * Format deadline message for AI
 */
export function formatDeadlineReminder(assignment: Assignment): string {
  const urgency = getDeadlineUrgency(assignment.dueDate);
  const emoji = urgency === 'urgent' ? '🚨' : urgency === 'soon' ? '⚠️' : '📅';
  
  return `${emoji} ${assignment.title} due ${assignment.dueDate}`;
}
